import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { multicastXhrConfig, removeQueryStr, IVbPlaybackTokenConfig, HlsUtilService } from '@vbrick/vbrick-player';

import { SecondMs } from 'rev-shared/date/Time.Constant';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { removeQueryParamsFromUrl } from 'rev-shared/util/UrlUtil';

const tokenExpireTimeMs = 10 * SecondMs;

@Injectable({ providedIn: 'root' })
export class HlsFileUtilService {
	private skipChildPlaylistCheck: boolean;
	private isPlaybackTokenAvailable: boolean;
	private useWithCredentials: boolean;
	private tokenStartTimeMs: number;
	private playbackTokenConfig: IVbPlaybackTokenConfig & { tokenFetchTimeMs: number };

	constructor(
		private readonly http: HttpClient,
		private readonly HlsUtilService: HlsUtilService
	) {}

	public waitForLivePlaybacks(playbacks: any[], timeoutMs: number, checkIntervalMs: number, skipChildPlaylistCheck?: boolean): Promise<any[]>{
		const newPlaybacks = Array.from(playbacks);
		let url: string = playbacks[0].url;
		this.playbackTokenConfig = playbacks[0]?.tokenConfig;
		const isMulticast: boolean = this.HlsUtilService.isMulticast(url, '');

		this.skipChildPlaylistCheck = skipChildPlaylistCheck;
		this.isPlaybackTokenAvailable = !!this.playbackTokenConfig;
		this.useWithCredentials = this.playbackTokenConfig?.withCredentials;
		this.tokenStartTimeMs = this.playbackTokenConfig?.tokenFetchTimeMs || Date.now();

		//remove withCred from query param.
		url = removeQueryParamsFromUrl(url, ['withCred']);

		return this.waitForLiveStream(url, timeoutMs, checkIntervalMs, isMulticast)
			.catch(err => {
				console.log(`Error ${err} ignored in waitForLiveStreamPlayback().`);
			})
			.then(refreshedUrl => {
				if (refreshedUrl) {
					newPlaybacks[0].url = refreshedUrl;
				}
				return newPlaybacks;
			});
	}

	private waitForLiveStream(url: string, timeoutMs: number, checkIntervalMs: number, isMulticast: boolean, startTimeMs: number = Date.now()): Promise<string> {
		if ((Date.now() - startTimeMs) > timeoutMs) {
			return Promise.reject(`Timed out for ${url}`);
		}

		return this.refreshToken(url)
			.then(refreshedUrl => this.checkLiveStream(refreshedUrl, timeoutMs, checkIntervalMs, isMulticast, startTimeMs))
			.then(refreshedUrl => {
				if (refreshedUrl) {
					console.log(`Stream ${url} is live after ${Date.now() - startTimeMs} ms.`);
					return refreshedUrl;
				}

				return this.waitAndCheckLiveStream(url, timeoutMs, checkIntervalMs, isMulticast, startTimeMs);
			})
			.catch(err => {
				console.error(`waitForStream error: ${err} for ${url}`);
				return undefined;
			});
	}

	private waitAndCheckLiveStream(url: string, timeoutMs: number, checkIntervalMs: number, isMulticast: boolean, startTimeMs: number): Promise<string> {
		return new Promise((resolve => window.setTimeout(resolve, checkIntervalMs)))
			.then(() => this.waitForLiveStream(url, timeoutMs, checkIntervalMs, isMulticast, startTimeMs));
	}

	private checkLiveStream(url: string, timeoutMs: number, checkIntervalMs: number, isMulticast: boolean, startTimeMs: number): Promise<string> {
		const sanitizedUrl: string = isMulticast ?
			removeQueryStr(url) :
			url;

		return lastValueFrom(this.http.get(sanitizedUrl, {
			headers: isMulticast ? multicastXhrConfig(url).additionalHeaders : undefined,
			responseType: 'text',
			withCredentials: this.isPlaybackTokenAvailable && this.useWithCredentials ? true : undefined
		}))
			.then(playlist => {
				if (playlist.includes('<?xml') && playlist.includes('<Error><Code>')) {
					return Promise.reject(`Invalid playlist %{url}.  It contains error code and message in XML format.`);
				}
				if (this.skipChildPlaylistCheck) {
					return url;
				}

				if (playlist.lastIndexOf('#EXT-X-ENDLIST') >= 0) {
					return undefined;
				} else if (playlist.includes('#EXT-X-STREAM-INF')){
					let childUrl = '';
					const parts = playlist.split('\n');

					for (let ii = 0; ii < parts.length; ii++) {
						if (parts[ii].includes('#EXT-X-STREAM-INF') && ii < parts.length - 1) {
							childUrl = this.getChildUrl(url, parts[ii + 1], isMulticast);
							break;
						}
					}

					if (childUrl === '') {
						return Promise.reject(`Invalid playlist %{url}`);
					}

					const result = this.checkLiveStream(childUrl, timeoutMs, checkIntervalMs, isMulticast, startTimeMs);
					if (result) {
						// Child url is good, return the original
						return url;
					}
					return result;
				}

				return url;
			})
			.catch(err => {
				if ((Date.now() - startTimeMs) > timeoutMs) {
					return Promise.reject(err);
				}

				return new Promise(resolve => window.setTimeout(resolve, checkIntervalMs))
					.then(() => this.refreshToken(url))
					.then(refreshedUrl => this.checkLiveStream(refreshedUrl, timeoutMs, checkIntervalMs, isMulticast, startTimeMs));
			});
	}

	private isAbsoluteUrl(url: string): boolean {
		return url.match('/^http[s]?:\/\//i') != null;
	}

	private getChildUrl(parentUrl: string, childUrlPart: string, isMulticast: boolean): string {
		if (this.isAbsoluteUrl(childUrlPart)) {
			return childUrlPart;
		}
		return this.constructChildUrl(parentUrl, childUrlPart, isMulticast);
	}

	private constructChildUrl(parentUrl: string, childUrlPart: string, isMulticast: boolean): string {
		let childUrl = '';
		let queryString = '';
		[childUrl, queryString] = parentUrl.split('?');

		const index = childUrl.lastIndexOf('/');
		if (index >= 0) {
			childUrl = childUrl.substr(0, index + 1);
		}

		childUrl += childUrlPart;
		if (isMulticast && queryString && queryString.length && !childUrl.includes('?')) {
			// VBMulticast playlist sometime contains child url part without any query string which is invalid in VBMulticast system.
			// We work around it by copying the query string from the parent.
			childUrl += `?${queryString}`;
		}
		return childUrl;
	}

	private refreshToken(url: string): Promise<string> {
		// We need to refresh CF token because it's short-lived.
		if (!this.isPlaybackTokenAvailable || (Date.now() - this.tokenStartTimeMs) < tokenExpireTimeMs) {
			return Promise.resolve(url);
		}
		const { path, resourceId, resourceType, tokenName, cnode } = this.playbackTokenConfig;
		return lastValueFrom(this.http.get(this.playbackTokenConfig.tokenEndpoint, {
			params: {
				resourceId,
				resourceType,
				tokenName,
				cnode,
				path
			},
			responseType: 'text'
		}))
			.then(newQueries => {
				this.tokenStartTimeMs = Date.now();
				const [baseUrl] = url.split('?');
				return `${baseUrl}?${newQueries}`;
			});
	}
}
